import type { ScoutDashboardMetricsQuery } from "generated-graphql/graphql";

export type ScoutDashboardRangeKey = "1h" | "3h" | "12h" | "1d" | "3d" | "1w";
export type ScoutDashboardRangeRequestSource = "initial" | "manual" | "poll";

export type ScoutDashboardRangeState = {
  rangeKey: ScoutDashboardRangeKey;
  startTime: Date;
  endTime: Date;
  periodSeconds: number;
};

export type ScoutDashboardMetrics = ScoutDashboardMetricsQuery["scoutDashboardMetrics"];

export type ScoutDashboardRangeRequestState = {
  displayRangeState: ScoutDashboardRangeState;
  requestedRangeState: ScoutDashboardRangeState;
  requestSource: ScoutDashboardRangeRequestSource;
};

export type ScoutDashboardCommittedRenderState = {
  rangeState: ScoutDashboardRangeState;
  metrics: ScoutDashboardMetrics;
};

export type ScoutDashboardState = ScoutDashboardRangeRequestState & {
  committedState: ScoutDashboardCommittedRenderState | null;
  hasManualRangeError: boolean;
};

export type ScoutDashboardAction =
  | { type: "requestManualRange"; rangeState: ScoutDashboardRangeState }
  | { type: "requestPoll"; rangeState: ScoutDashboardRangeState }
  | { type: "metricsCompleted"; metrics: ScoutDashboardMetrics }
  | { type: "metricsErrored" };

function getTimeMs(value: string | Date): number | null {
  const date = value instanceof Date ? value : new Date(value);
  const timeMs = date.getTime();
  return Number.isFinite(timeMs) ? timeMs : null;
}

export function getScoutDashboardRangeIdentity(rangeState: ScoutDashboardRangeState): string {
  return [
    rangeState.rangeKey,
    rangeState.startTime.toISOString(),
    rangeState.endTime.toISOString(),
    rangeState.periodSeconds,
  ].join("|");
}

export function scoutDashboardMetricsMatchesRange(
  metrics: ScoutDashboardMetrics,
  rangeState: ScoutDashboardRangeState
): boolean {
  return (
    metrics.periodSeconds === rangeState.periodSeconds &&
    getTimeMs(metrics.startTime) === rangeState.startTime.getTime() &&
    getTimeMs(metrics.endTime) === rangeState.endTime.getTime()
  );
}

/**
 * Chooses the metrics/range pair that is safe to render together.
 *
 * Apollo may expose a new `data` object on a different render from the range state update that
 * requested it. Rendering only matching metrics keeps the x-axis domain and line points in sync;
 * otherwise the dashboard keeps the previous committed chart data visible while the request
 * finishes. This avoids the one-frame distorted line flash during large range switches.
 */
export function selectScoutDashboardRenderState({
  committedState,
  metrics,
  requestedRangeState,
}: {
  committedState: ScoutDashboardCommittedRenderState | null;
  metrics: ScoutDashboardMetrics | undefined;
  requestedRangeState: ScoutDashboardRangeState;
}): ScoutDashboardCommittedRenderState | null {
  if (metrics && scoutDashboardMetricsMatchesRange(metrics, requestedRangeState)) {
    return {
      rangeState: requestedRangeState,
      metrics,
    };
  }

  return committedState;
}

export function createInitialScoutDashboardState(
  initialRangeState: ScoutDashboardRangeState
): ScoutDashboardState {
  return {
    displayRangeState: initialRangeState,
    requestedRangeState: initialRangeState,
    requestSource: "initial",
    committedState: null,
    hasManualRangeError: false,
  };
}

/**
 * Keeps the URL aligned with the range the user can actually see.
 *
 * Manual range clicks should update `?range=` only after the matching metrics response is ready to
 * commit. Failed, stale, or out-of-order responses leave the URL on the last displayed range.
 */
export function shouldUpdateScoutDashboardUrlOnMetricsCompleted(
  state: ScoutDashboardState,
  metrics: ScoutDashboardMetrics
): boolean {
  return (
    state.requestSource === "manual" &&
    scoutDashboardMetricsMatchesRange(metrics, state.requestedRangeState)
  );
}

/**
 * React reducer for Scout dashboard range requests and metric response events.
 *
 * The important invariant is that `committedState` always pairs metrics with the exact range that
 * produced them. Completion events that do not match the current request are ignored, so stale or
 * out-of-order responses cannot advance the visible range or briefly render against the wrong
 * chart domain.
 */
export function reduceScoutDashboardRangeState(
  state: ScoutDashboardState,
  action: ScoutDashboardAction
): ScoutDashboardState {
  if (action.type === "requestManualRange") {
    return {
      ...state,
      requestedRangeState: action.rangeState,
      requestSource: "manual",
      hasManualRangeError: false,
    };
  }

  if (action.type === "requestPoll") {
    return {
      ...state,
      requestedRangeState: action.rangeState,
      requestSource: "poll",
    };
  }

  if (action.type === "metricsCompleted") {
    if (!scoutDashboardMetricsMatchesRange(action.metrics, state.requestedRangeState)) {
      return state;
    }

    return {
      ...state,
      displayRangeState: state.requestedRangeState,
      requestSource: "initial",
      committedState: {
        rangeState: state.requestedRangeState,
        metrics: action.metrics,
      },
      hasManualRangeError: false,
    };
  }

  if (state.requestSource !== "manual") {
    return state;
  }

  return {
    ...state,
    requestedRangeState: state.displayRangeState,
    requestSource: "initial",
    hasManualRangeError: true,
  };
}
